import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DocumentDataTable from './DocumentDataTable';
import { deleteUploadedFile } from '../api/uploadAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { getTasksByAsset } from '../api/tasksAPI';
import { useValidator } from '../utils/useValidator';
import { getAll } from '../api';
import { createStockToRepairStatusMap } from '../shared/rotableStatusMaps';

const initialFormState = {
  notes: '',
  files: [],
  images: [],
  assetId: null,
  taskId: null,
  stockItemStatusId: null,
  repairStatusId: null,
  locationId: null,
};

const RotableTransactionModal = ({
  show,
  action,
  rotable,
  onClose,
  onSave,
}) => {
  const [formState, setFormState] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [stockStatusOptions, setStockStatusOptions] = useState([]);
  const [repairStatusOptions, setRepairStatusOptions] = useState([]);
  const [imageModalSrc, setImageModalSrc] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const locationActions = useMemo(
    () => new Set(['receive', 'remove', 'quarantine', 'awaiting-pickup', 'adjustment']),
    [],
  );

  const normalizeStatusName = useCallback(
    (value) => String(value || '').trim().toLowerCase(),
    [],
  );
  const stockToRepairStatusMap = useMemo(
    () => createStockToRepairStatusMap(normalizeStatusName),
    [normalizeStatusName],
  );

  useEffect(() => {
    if (show) {
      const isQuarantineRelease = action?.key === 'quarantine-release';
      setFormState({
        ...initialFormState,
        assetId: rotable?.assetId ?? null,
        taskId: rotable?.task?.id ?? null,
        stockItemStatusId: isQuarantineRelease ? 20 : rotable?.statusId ?? null,
        repairStatusId: isQuarantineRelease ? 2 : rotable?.repairStatusId ?? null,
        locationId: rotable?.locationId ?? null,
      });
      setSaving(false);
    }
  }, [show, action, rotable]);

  const validationFields = useMemo(() => {
    if (action?.key === 'fit') {
      return [
        { field: 'assetId', label: 'Asset', required: true },
        { field: 'taskId', label: 'Task', required: true },
      ];
    }
    if (action?.key === 'adjustment') {
      return [
        { field: 'stockItemStatusId', label: 'Stock Item Status', required: true },
        { field: 'repairStatusId', label: 'Repair Status', required: true },
        { field: 'notes', label: 'Notes', required: true },
      ];
    }
    if (action?.key === 'quarantine-release') {
      return [
        { field: 'stockItemStatusId', label: 'Stock Item Status', required: true },
        { field: 'repairStatusId', label: 'Repair Status', required: true },
      ];
    }
    if (action?.key === 'awaiting-pickup') {
      return [{ field: 'repairStatusId', label: 'Repair Status', required: true }];
    }
    return [];
  }, [action?.key]);

  const { errors, validate, resetErrors } = useValidator(validationFields, formState);

  useEffect(() => {
    if (show) {
      resetErrors();
    }
  }, [resetErrors, show]);

  useEffect(() => {
    if (action?.key !== 'fit') {
      setTasks([]);
      setTasksLoading(false);
      return;
    }

    const assetId = formState.assetId;
    if (!assetId) {
      setTasks([]);
      setTasksLoading(false);
      setFormState((prev) => (prev.taskId ? { ...prev, taskId: null } : prev));
      return;
    }

    let isActive = true;
    setTasksLoading(true);
    getTasksByAsset(assetId)
      .then((data) => {
        if (!isActive) return;
        const filtered = (Array.isArray(data) ? data : []).filter(
          (task) => task?.taskType?.component === 1,
        );
        setTasks(filtered);
        setFormState((prev) =>
          filtered.some((task) => Number(task.id) === Number(prev.taskId))
            ? prev
            : { ...prev, taskId: null },
        );
      })
      .catch((err) => {
        console.error('Error loading tasks for asset', err);
        if (!isActive) return;
        setTasks([]);
      })
      .finally(() => {
        if (isActive) {
          setTasksLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [action?.key, formState.assetId]);

  useEffect(() => {
    if (!show || !['adjustment', 'awaiting-pickup', 'quarantine-release'].includes(action?.key)) {
      setStockStatusOptions([]);
      setRepairStatusOptions([]);
      return;
    }
    let isMounted = true;
    const loadLookups = async () => {
      try {
        const [stockStatusResponse, repairStatusResponse] = await Promise.all([
          getAll('stock-item-status'),
          getAll('repair-status'),
        ]);
        if (!isMounted) return;
        const stockItems = Array.isArray(stockStatusResponse?.data)
          ? stockStatusResponse.data
          : Array.isArray(stockStatusResponse)
            ? stockStatusResponse
            : [];
        const repairItems = Array.isArray(repairStatusResponse?.data)
          ? repairStatusResponse.data
          : Array.isArray(repairStatusResponse)
            ? repairStatusResponse
            : [];
        const serialisedOnlyStockItems = stockItems.filter(
          (item) => Number(item?.serialisedOnly) === 1,
        );
        setStockStatusOptions(
          serialisedOnlyStockItems.map((item) => ({
            value: item.id,
            label: item.name,
            raw: item,
          })),
        );
        setRepairStatusOptions(
          repairItems.map((item) => ({
            value: item.id,
            label: item.name,
            raw: item,
          })),
        );
      } catch (err) {
        console.error('Error fetching status lookups:', err);
        if (!isMounted) return;
        setStockStatusOptions([]);
        setRepairStatusOptions([]);
      }
    };

    loadLookups();
    return () => {
      isMounted = false;
    };
  }, [action?.key, show]);

  const selectedStockStatusName = useMemo(() => {
    if (action?.key === 'quarantine-release' && !formState.stockItemStatusId) {
      return '';
    }
    const option = stockStatusOptions.find(
      (statusOption) => String(statusOption.value) === String(formState.stockItemStatusId),
    );
    return option?.raw?.name || rotable?.status?.name || '';
  }, [action?.key, formState.stockItemStatusId, rotable?.status?.name, stockStatusOptions]);

  const allowedRepairStatusNames = useMemo(() => {
    if (!['adjustment', 'quarantine-release'].includes(action?.key)) {
      return null;
    }
    const normalized = normalizeStatusName(selectedStockStatusName);
    const allowed = stockToRepairStatusMap[normalized];
    return allowed ? new Set(allowed) : null;
  }, [action?.key, normalizeStatusName, selectedStockStatusName, stockToRepairStatusMap]);

  const filteredRepairStatusOptions = useMemo(() => {
    if (!allowedRepairStatusNames) {
      return repairStatusOptions;
    }
    return repairStatusOptions.filter((option) => {
      const optionName = normalizeStatusName(option?.raw?.name || option?.label);
      return allowedRepairStatusNames.has(optionName);
    });
  }, [allowedRepairStatusNames, normalizeStatusName, repairStatusOptions]);

  const awaitingPickupRepairStatusOptions = useMemo(() => {
    if (action?.key !== 'awaiting-pickup') {
      return repairStatusOptions;
    }
    const allowedIds = new Set([2, 17, 18]);
    return repairStatusOptions.filter((option) => allowedIds.has(Number(option.value)));
  }, [action?.key, repairStatusOptions]);

  useEffect(() => {
    if (!['adjustment', 'quarantine-release'].includes(action?.key)) {
      return;
    }
    if (!allowedRepairStatusNames || !formState.repairStatusId) {
      return;
    }
    const currentOption = repairStatusOptions.find(
      (option) => String(option.value) === String(formState.repairStatusId),
    );
    const currentName = normalizeStatusName(currentOption?.raw?.name || currentOption?.label);
    if (currentName && !allowedRepairStatusNames.has(currentName)) {
      setFormState((prev) => ({ ...prev, repairStatusId: null }));
    }
  }, [
    action?.key,
    allowedRepairStatusNames,
    formState.repairStatusId,
    normalizeStatusName,
    repairStatusOptions,
  ]);

  const quarantineReleaseStockStatusOptions = useMemo(() => {
    if (action?.key !== 'quarantine-release') {
      return stockStatusOptions;
    }
    const allowedIds = new Set([20, 1, 17, 18]);
    return stockStatusOptions.filter((option) => allowedIds.has(Number(option.value)));
  }, [action?.key, stockStatusOptions]);

  useEffect(() => {
    if (action?.key !== 'quarantine-release') {
      return;
    }
    if (!formState.stockItemStatusId) {
      return;
    }
    const isAllowed = quarantineReleaseStockStatusOptions.some(
      (option) => String(option.value) === String(formState.stockItemStatusId),
    );
    if (!isAllowed) {
      setFormState((prev) => ({ ...prev, stockItemStatusId: null, repairStatusId: null }));
    }
  }, [action?.key, formState.stockItemStatusId, quarantineReleaseStockStatusOptions]);

  useEffect(() => {
    if (action?.key !== 'awaiting-pickup') {
      return;
    }
    if (awaitingPickupRepairStatusOptions.length === 0) {
      return;
    }
    const hasValidSelection = awaitingPickupRepairStatusOptions.some(
      (option) => Number(option.value) === Number(formState.repairStatusId),
    );
    if (!hasValidSelection) {
      const defaultOption = awaitingPickupRepairStatusOptions.find(
        (option) => Number(option.value) === 2,
      );
      setFormState((prev) => ({
        ...prev,
        repairStatusId: defaultOption?.value ?? awaitingPickupRepairStatusOptions[0].value,
      }));
    }
  }, [action?.key, awaitingPickupRepairStatusOptions, formState.repairStatusId]);

  const taskOptions = useMemo(
    () =>
      tasks.map((task) => ({
        value: task.id,
        label: `${task?.code?.code ? `${task.code.code} - ` : ''}${task?.name || `Task ${task.id}`}`,
      })),
    [tasks],
  );

  const formFields = useMemo(
    () => {
      const fields = [];

      if (action?.key === 'fit') {
        fields.push({
          field: 'assetId',
          label: 'Asset',
          type: 'select',
          size: 12,
          api: 'assets/for-dropdown',
          displayField: (asset) =>
            `${asset.unitNumber || asset.assetNumber || ''} ${asset.assetDescription || ''}`.trim(),
          isClearable: true,
          required: true,
        });
        fields.push({
          field: 'taskId',
          label: 'Task',
          type: 'select',
          size: 12,
          options: taskOptions,
          disabled: !formState.assetId,
          isClearable: true,
          isLoading: tasksLoading,
          required: true,
        });
      }

      if (action?.key === 'adjustment') {
        fields.push({
          field: 'stockItemStatusId',
          label: 'Stock Item Status',
          type: 'select',
          size: 12,
          options: stockStatusOptions,
          required: true,
          isClearable: true,
        });
        fields.push({
          field: 'repairStatusId',
          label: 'Repair Status',
          type: 'select',
          size: 12,
          options: filteredRepairStatusOptions,
          required: true,
          isClearable: true,
        });
      }

      if (action?.key === 'quarantine-release') {
        fields.push({
          field: 'stockItemStatusId',
          label: 'Stock Item Status',
          type: 'select',
          size: 12,
          options: quarantineReleaseStockStatusOptions,
          required: true,
          isClearable: true,
        });
        fields.push({
          field: 'repairStatusId',
          label: 'Repair Status',
          type: 'select',
          size: 12,
          options: filteredRepairStatusOptions,
          required: true,
          isClearable: true,
        });
      }

      if (action?.key === 'awaiting-pickup') {
        fields.push({
          field: 'repairStatusId',
          label: 'Repair Status',
          type: 'select',
          size: 12,
          options: awaitingPickupRepairStatusOptions,
          required: true,
          isClearable: true,
        });
      }

      if (locationActions.has(action?.key)) {
        fields.push({
          field: 'locationId',
          label: 'Location',
          type: 'select',
          size: 12,
          api: 'locations',
          displayField: 'name',
          isClearable: true,
        });
      }

      fields.push(
        {
          field: 'notes',
          label: 'Transaction Notes',
          type: 'textarea',
          size: 12,
          lines: 4,
          required: action?.key === 'adjustment',
        },
        {
          field: 'files',
          label: 'Files',
          type: 'files',
          size: 12,
          directory: 'partsfiles',
          accept:
            '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
        },
        {
          field: 'images',
          label: 'Images',
          type: 'images',
          size: 12,
          directory: 'partsfiles',
          onImageClick: (src) => {
            setImageModalSrc(src);
            setShowImageModal(true);
          },
        },
      );

      return fields;
    },
    [
      action?.key,
      awaitingPickupRepairStatusOptions,
      filteredRepairStatusOptions,
      formState.assetId,
      quarantineReleaseStockStatusOptions,
      stockStatusOptions,
      taskOptions,
      tasksLoading,
      locationActions,
    ],
  );

  const { renderField } = useFormBuilder(formFields, formState, setFormState, errors);

  const transactionDescription = useMemo(() => {
    const descriptions = {
      receive:
        'This transaction receives a rotable component from a supplier into stock. The rotable item status will be updated to In Stock and the repair status will be set to Serviceable.',
      fit:
        'This transaction fits a rotable component to an asset. The rotable item status will be updated to Fitted and the repair status will be set to In Service. The asset number and the component the rotable is fitted to will be recorded as part of the transaction.',
      remove:
        'This transaction removes a rotable component from an asset. The rotable item status will be updated to In Stock and the repair status will be set to Serviceable. The asset number and the component the rotable was removed from will be recorded as part of the transaction.',
      quarantine:
        'This transaction quarantines a rotable component. The rotable item status will be updated to In Quarantine and the repair status will be set to Unserviceable.',
      'quarantine-release':
        'This transaction releases a rotable component from quarantine. Choose the new stock item status and repair status to reflect the component condition.',
      'awaiting-pickup':
        'This transaction marks a rotable component as Awaiting Pickup. The repair status selected will be recorded to track the next step in the repair process.',
      'send-to-supplier':
        'This transaction sends a rotable component to a supplier. The rotable item status will be updated to At Supplier and the repair status will be set to Under Repair.',
      scrap:
        'This transaction scraps a rotable component. The rotable item status will be updated to Scrapped and the repair status will be set to Left The System.',
      sell:
        'This transaction sells a rotable component. The rotable item status will be updated to Sold and the repair status will be set to Left The System.',
      adjustment:
        'This transaction adjusts the rotable component statuses. Use it to align the stock item status and repair status with the actual component condition. Use this when rotable transactions have occured out of sequence or to correct errors.',
    };

    return action?.key ? descriptions[action.key] : null;
  }, [action?.key]);

  const handleFileDelete = async (filename, index) => {
    if (!window.confirm('Delete this file?')) return;
    setFormState((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
    try {
      await deleteUploadedFile(filename, 'partsfiles');
    } catch (err) {
      console.error('Error deleting file', err);
    }
  };

  const handleSave = async () => {
    if (!onSave) return;
    const isValid = validate();
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave({
        notes: formState.notes || '',
        files: formState.files || [],
        images: formState.images || [],
        assetId: formState.assetId ?? null,
        taskId: formState.taskId ?? null,
        stockItemStatusId: formState.stockItemStatusId ?? null,
        repairStatusId: formState.repairStatusId ?? null,
        locationId: formState.locationId ?? null,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal modal-backdrop show d-block" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {action?.label ? `${action.label}` : 'Rotable Transaction'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
          </div>
          <div className="modal-body">
            <div className="row g-3 mb-16">
              <div className="col-md-12">
                {transactionDescription && (
                  <p className="text-muted mb-0">{transactionDescription}</p>
                )}
              </div>
            </div>

            <div className="row g-3">
              {formFields.map(renderField)}
            </div>
            {Array.isArray(formState.files) && formState.files.length > 0 && (
              <DocumentDataTable
                files={formState.files}
                onDelete={handleFileDelete}
                directory="partsfiles"
              />
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      {showImageModal && (
        <div
          className="modal modal-backdrop show d-block"
          onClick={() => setShowImageModal(false)}
          tabIndex="-1"
        >
          <div className="modal-dialog" style={{ maxWidth: '75%' }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <img src={imageModalSrc} alt="view" style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default RotableTransactionModal;
